import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AsyncBoundary, Icon } from '@kollez/design-system';
import { useGroupAnalytics, useNirfExport } from '@/lib/queries';
import { formatLPA } from '@kollez/shared';

/**
 * /group/overview (F87a) — direct port of kollez/pages/group/dashboard.html.
 *   5 KPIs · Colleges filterbar/table with .pct-cell + target bars + sparkline
 *   Sidebar: Needs-your-attention · Top recruiters · Nexie strip
 */

type SortKey = 'name' | 'pct' | 'ctc' | 'drives';

export function GroupOverviewPage() {
  const { data, isLoading, error, refetch } = useGroupAnalytics();
  const nirf = useNirfExport();
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [sort, setSort] = useState<SortKey>('pct');
  const [dir, setDir] = useState(1);

  const rows = data?.colleges ?? [];
  const totals = data?.totals ?? { colleges: 0, drives: 0, offersAccepted: 0, applications: 0 };

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const filtered = rows.filter((c) => {
      const okQ = !ql || `${c.name} ${c.type ?? ''}`.toLowerCase().includes(ql);
      const okType = !type || c.type === type;
      return okQ && okType;
    });
    const sorted = [...filtered].sort((a, b) => {
      const av = sort === 'name' ? a.name : sort === 'pct' ? a.offersAccepted / Math.max(1, a.drives)
        : sort === 'ctc' ? a.avgCtc : a.drives;
      const bv = sort === 'name' ? b.name : sort === 'pct' ? b.offersAccepted / Math.max(1, b.drives)
        : sort === 'ctc' ? b.avgCtc : b.drives;
      return (av > bv ? 1 : -1) * dir;
    });
    return sorted;
  }, [rows, q, type, sort, dir]);

  const behind = rows.filter((c) => (c.offersAccepted / Math.max(1, c.drives)) < 0.5).slice(0, 3);
  const topRecruiters = useMemo(() => {
    // Approximate: rank by offersIssued
    return [...rows].sort((a, b) => b.offersIssued - a.offersIssued).slice(0, 5);
  }, [rows]);

  function toggleSort(k: SortKey) {
    if (sort === k) setDir((d) => -d);
    else { setSort(k); setDir(1); }
  }

  return (
    <div className="stack">
      <style>{`
        .gv { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1200px) { .gv { grid-template-columns: minmax(0,1fr) 320px; } }
        .pct-cell { display: grid; grid-template-columns: 44px minmax(80px, 160px); gap: 10px; align-items: center; }
        .att li { display: grid; grid-template-columns: minmax(0,1fr) auto; gap: 8px; padding: 12px 16px; border-bottom: 1px solid rgb(var(--line-tertiary)); align-items: center; }
        .att li:last-child { border-bottom: 0; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Group · {totals.colleges} colleges · season 2026–27</p>
          <h1 className="h-page">Placements across the group</h1>
          <p className="subhead">
            {behind.length > 0
              ? `${behind.length} colleges behind target — start with ${behind[0]!.name}.`
              : 'Every college is at or ahead of target.'}
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => nirf.mutate()} disabled={nirf.isPending}>
            <Icon name="download" size={14} />{nirf.isPending ? 'Queuing…' : 'NIRF export'}
          </button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <div className="card kpi">
          <span className="kpi-label">Colleges</span>
          <span className="kpi-value">{totals.colleges}</span>
          <span className="kpi-meta">this season</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Drives</span>
          <span className="kpi-value">{totals.drives}</span>
          <span className="kpi-meta">across all colleges</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Offers accepted</span>
          <span className="kpi-value">{totals.offersAccepted}</span>
          <span className="kpi-meta">this season</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Applications</span>
          <span className="kpi-value">{totals.applications.toLocaleString('en-IN')}</span>
          <span className="kpi-meta">student submissions</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Behind target</span>
          <span className="kpi-value">{behind.length}</span>
          <span className="kpi-meta">under 50% offer/drive</span>
        </div>
      </div>

      <div className="gv">
        <section className="card min-w-0">
          <div className="filterbar">
            <h2 className="h-section mr-2">Colleges</h2>
            <div className="input-group fb-search">
              <Icon name="search" size={14} />
              <input className="input" placeholder="College or city" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All types</option>
              <option>Engineering</option>
              <option>Pharmacy</option>
              <option>Management</option>
              <option>Degree</option>
            </select>
            <span className="fb-count">{shown.length} shown</span>
          </div>
          <AsyncBoundary
            kind="table"
            inCard
            loading={isLoading}
            error={error}
            empty={!isLoading && shown.length === 0}
            emptyTitle="No colleges yet"
            onRetry={() => void refetch()}
          >
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th><button onClick={() => toggleSort('name')}>College</button></th>
                    <th><button onClick={() => toggleSort('pct')}>Placed rate</button></th>
                    <th className="r hide-mobile"><button onClick={() => toggleSort('ctc')}>Median CTC</button></th>
                    <th className="r hide-mobile"><button onClick={() => toggleSort('drives')}>Drives</button></th>
                    <th className="hide-mobile">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c) => {
                    const rate = c.drives > 0 ? Math.round((c.offersAccepted / c.drives) * 100) : 0;
                    const gap = rate - 60;
                    const tone = gap <= -30 ? 'danger' : gap < 0 ? 'warning' : 'success';
                    return (
                      <tr key={c.collegeId}>
                        <td>
                          <div className="cell-title">{c.name}</div>
                          <div className="cell-sub">
                            {c.type ?? '—'}
                            {tone === 'danger' && <> · <span style={{ color: 'rgb(var(--ink-danger))', fontWeight: 500 }}>Behind</span></>}
                          </div>
                        </td>
                        <td>
                          <div className="pct-cell">
                            <b className="num" style={{ color: tone === 'danger' ? 'rgb(var(--ink-danger))' : undefined }}>{rate}%</b>
                            {/* F-089: previous version drew a target guide at 60% for every
                                college regardless of what target was actually set. College
                                has no target field yet — dropped the guide line; keep a plain
                                progress bar until targets:manage endpoint lands. */}
                            <div className={`bar ${tone}`}>
                              <span style={{ width: `${Math.min(100, rate)}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="r hide-mobile">{c.avgCtc ? formatLPA(c.avgCtc) : '—'}</td>
                        <td className="r hide-mobile">{c.drives}</td>
                        <td className="hide-mobile">
                          {/* F-089: previous version drew an invented up-trending polyline
                              per college. There's no per-college time-series data source
                              today; sparkline dropped until /group/analytics/trends is
                              extended with per-college points. */}
                          <span className="cell-sub">—</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </AsyncBoundary>
        </section>

        <div className="grid gap-5 min-w-0">
          <section className="card">
            <div className="card-head"><h2 className="h-section">Needs your attention</h2></div>
            <ul className="att m-0 p-0 list-none">
              {behind.map((c) => (
                <li key={c.collegeId}>
                  <div className="min-w-0">
                    <div className="cell-title">{c.name}</div>
                    <div className="cell-sub">
                      {c.drives > 0 ? `${Math.round((c.offersAccepted / c.drives) * 100)}% offers/drive` : 'No drives yet'} · {c.drives} drives
                    </div>
                  </div>
                  <button className="btn btn-outline btn-sm">Note to principal</button>
                </li>
              ))}
              {behind.length === 0 && (
                <li>
                  <div>
                    <div className="cell-title">All colleges on track</div>
                    <div className="cell-sub">No principal notes queued.</div>
                  </div>
                  <span className="badge badge-success">✓</span>
                </li>
              )}
            </ul>
          </section>

          <section className="card">
            <div className="card-head"><h2 className="h-section">Top recruiters</h2><span className="helper">by offers</span></div>
            <div className="card-pad grid gap-3">
              {topRecruiters.map((c) => (
                <div key={c.collegeId} className="flex items-baseline justify-between">
                  <div className="cell-title truncate">{c.name}</div>
                  <span className="cell-sub">{c.offersIssued}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Deep-audit fix (Page 49/N): the Nexie strip's suggested
              prompts named specific tenants — 'Why is pharmacy behind
              this year?' and 'Which companies hired from GIT but not
              GEC?' A group admin at a tenant that has no pharmacy
              department or no GIT/GEC colleges would read those
              suggestions as authoritative and confusing. Rewrote to
              generic prompts that describe the shape of what to ask. */}
          <Link to="/nexie" className="ai-suggest">
            <Icon name="sparkles" size={14} />
            <div><b>Ask Nexie</b> — "Which department is trailing this year?" or "Which recruiters visited some of my colleges but not others?"</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
